'use client';

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Bell, Check, Clock, Loader2, AlertCircle, AlertTriangle, ChevronDown, X, RefreshCw, ExternalLink, Pause, Repeat,
  RotateCcw, type LucideIcon,
} from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationProvider';
import { cn } from '@/lib/utils';
import { colors } from '@/lib/theme/colors';
import { statusColors } from '@/lib/theme/status-colors';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { BaseSheet } from '@/components/shared/sheets/BaseSheet';
import { Skeleton } from '@/components/ui/skeleton';
import Link from 'next/link';
import { PATH } from '@/constants/path';
import { useGetRunDetails, useGetRecentActivity } from '@/hooks/usePlaysQuery';
import { useGetAudienceById } from '@/hooks/useAudiencesQuery';
import { RunExecution, ExecutionStatus, RunStatus } from '@/types/execution-results';
import { deriveStepMetrics, customerStepError } from '@/views/play-builder/components/results/config/resultDisplay';
import {
  formatWorkflowStatus, getCampaignDisplayStatus, getCampaignPeopleTotal, WORKFLOW_STATUS_BADGE_STYLE,
} from '@/utils/audiences';
import { getPlayDisplayName } from '@/utils/playDisplayNames';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import {
  useAePreparedNotifications,
  useMarkAePreparedNotificationRead,
  useMarkAllAePreparedNotificationsRead,
} from '@/hooks/useAePreparedNotifications';
import type { AePreparedNotification } from '@/services/aeNotifications';

interface NotificationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Headline icon for an audience's display status (campaign-level), matching the
 * vocabulary used on /campaigns and the /workflows/:id header.
 */
const getAudienceStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
    case 'replied':
      return <Check className="h-4 w-4" style={{ color: statusColors.success.icon }} />;
    case 'in_progress':
      return <Loader2 className="h-4 w-4 animate-spin" style={{ color: statusColors.info.icon }} />;
    case 'paused':
      return <Pause className="h-4 w-4 text-slate-500" />;
    case 'stalled':
    case 'error':
      return <AlertCircle className="h-4 w-4" style={{ color: statusColors.error.icon }} />;
    case 'partial':
    case 'ready':
    case 'new':
    default:
      return <Clock className="h-4 w-4" style={{ color: statusColors.warning.icon }} />;
  }
};

/**
 * Get status icon based on execution status (run-step level)
 */
const getStatusIcon = (status: ExecutionStatus | RunStatus | string) => {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4" style={{ color: statusColors.success.icon }} />;
    case 'failed':
    case 'stalled':
      return <AlertCircle className="h-4 w-4" style={{ color: statusColors.error.icon }} />;
    case 'in_progress':
      return <Loader2 className="h-4 w-4 animate-spin" style={{ color: statusColors.info.icon }} />;
    case 'pending':
    case 'partial':
    default:
      return <Clock className="h-4 w-4" style={{ color: statusColors.warning.icon }} />;
  }
};

/**
 * Get badge variant based on status (run-step level)
 */
const getStatusBadgeVariant = (status: ExecutionStatus | RunStatus | string): 'success' | 'error' | 'info' | 'warning' | 'neutral' => {
  switch (status) {
    case 'completed':
    case 'success':
    case 'email_sent':
    case 'email_drafted':
    case 'test_email_generated':
    case 'test_video_generated':
      return 'success';
    case 'skipped':
      return 'neutral';
    case 'failed':
    case 'error':
    case 'cancelled':
    case 'stalled':
      return 'error';
    case 'in_progress':
    case 'processing':
    case 'running':
    case 'started':
    case 'generating_content':
      return 'info';
    case 'scheduled':
    case 'waiting':
    case 'pending':
    case 'partial':
    default:
      return 'warning';
  }
};

/**
 * Get user-friendly label for execution/run status (run-step level)
 */
const getStatusLabel = (status: ExecutionStatus | RunStatus | string): string => {
  switch (status) {
    case 'completed':
    case 'success':
      return 'Completed';
    case 'email_sent':
      return 'Email Sent';
    case 'email_drafted':
      return 'Email Drafted';
    case 'test_email_generated':
      return 'Test Ready';
    case 'test_video_generated':
      return 'Video Ready';
    case 'skipped':
      return 'Skipped';
    case 'scheduled':
      return 'Scheduled';
    case 'failed':
      return 'Failed';
    case 'error':
      return 'Error';
    case 'cancelled':
      return 'Cancelled';
    case 'in_progress':
      return 'In Progress';
    case 'processing':
      return 'Processing';
    case 'running':
      return 'Running';
    case 'started':
      return 'Starting';
    case 'generating_content':
      return 'Generating';
    case 'waiting':
      return 'Waiting';
    case 'pending':
      return 'Pending';
    case 'partial':
      return 'Partial';
    case 'stalled':
      return 'Stalled';
    default:
      return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

/**
 * Format timestamp to readable time
 */
const formatTime = (ts: string) => {
  try {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

/**
 * Get human-readable play type name
 */
const getPlayTypeName = (playType: string): string => {
  const typeMap: Record<string, string> = {
    ai_agent: 'AI Agent',
    email_campaign: 'Email',
    sms_campaign: 'SMS',
    linkedin_campaign: 'LinkedIn',
    voice_campaign: 'Voice',
    dialer: 'Human Dialer',
    delay: 'Delay',
    heygen_video: 'Video',
  };
  return typeMap[playType] || playType;
};

/**
 * Notification panel filter modes. 'all' preserves the original behavior (every step);
 * 'campaigns' hides AI/research "task" steps so only email + campaign outreach shows.
 */
type NotifFilter = 'all' | 'campaigns';
const NOTIF_FILTER_STORAGE_KEY = 'mb_notifications_play_filter';

// AI/research "task" play types hidden by the campaigns-only filter. Use a blocklist (not an
// allowlist) so any current or future outreach play type — email_campaign, outreach_campaign,
// heyreach_campaign and its heyreach_* steps, voicemail_drop, crm_add, delay, … — is always
// kept; only these AI/research result steps are treated as task notifications. play_type maps to
// plays.action_type (verified against prod). Taxonomy is intentionally conservative; extend per
// product guidance if more step types should count as "tasks".
const TASK_PLAY_TYPES = new Set([
  'ai_agent', 'heygen_video', 'video_editor', 'find_materials', 'find_email', 'apollo_prospect',
  'prospect', 'serp_search', 'linkedin_enrichment', 'browser_automation', 'email_scan',
  'google_drive_scan', 'calendar_worker', 'google_sheets',
]);

const isTaskExecution = (execution: { play_type?: string | null }): boolean =>
  TASK_PLAY_TYPES.has(execution.play_type || '');

/**
 * Execution card component - displays a single play execution
 */
const ExecutionCard: React.FC<{
  execution: RunExecution;
  isTestRun: boolean;
}> = ({ execution, isTestRun }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasResults = execution.results && execution.results.length > 0;

  // Honest metric summary — shared single source of truth with the Result tab
  // and WorkflowExecutionSheet (config/resultDisplay.deriveStepMetrics), so the
  // notification card shows the same accurate string as the rest of the app.
  const metricsSummary = deriveStepMetrics(execution).summary;

  return (
    <div
      className="rounded-lg border transition-all hover:shadow-sm"
      style={{ borderColor: colors.border.light }}
    >
      <button
        className={cn(
          "w-full p-3 flex items-start gap-3 text-left transition-colors",
          hasResults ? "rounded-t-lg" : "rounded-lg",
          "hover:bg-slate-50 active:bg-slate-100",
          hasResults && isExpanded && "border-b",
        )}
        style={{ borderColor: colors.border.light }}
        onClick={() => hasResults && setIsExpanded(!isExpanded)}
        disabled={!hasResults}
      >
        <div className="mt-0.5 flex-shrink-0">
          {getStatusIcon(execution.status)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4
              className="text-sm font-medium truncate flex-1"
              style={{ color: colors.text.primary }}
            >
              {getPlayDisplayName(execution.play_type, execution.play_name || getPlayTypeName(execution.play_type))}
            </h4>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="text-xs tabular-nums"
                style={{ color: colors.text.quaternary }}
              >
                {execution.executed_at ? formatTime(execution.executed_at) : ''}
              </span>
              {hasResults && (
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform flex-shrink-0",
                    isExpanded && "rotate-180"
                  )}
                  style={{ color: colors.text.quaternary }}
                />
              )}
            </div>
          </div>

          {/* Status and metrics row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={getStatusBadgeVariant(execution.status)} size="sm">
              {getStatusLabel(execution.status)}
            </Badge>
            {execution.sequence_order && (
              <span
                className="text-xs"
                style={{ color: colors.text.tertiary }}
              >
                Step {execution.sequence_order}
              </span>
            )}
            {metricsSummary && (
              <span
                className="text-xs"
                style={{ color: colors.text.tertiary }}
              >
                {metricsSummary}
              </span>
            )}
          </div>

          {/* Error message if failed */}
          {execution.error && (
            <p
              className="text-xs mt-2 line-clamp-2"
              style={{ color: statusColors.error.text }}
            >
              {customerStepError(execution)}
            </p>
          )}
        </div>
      </button>

      {/* Expandable results */}
      {hasResults && isExpanded && (
        <div className="px-3 py-2 space-y-2 bg-slate-50/50">
          {execution.results.slice(0, 10).map((result: any, index: number) => {
            const email = result.email || result.recipient_email || '';
            const status = result.status || 'processed';
            const subject = result.subject || '';

            return (
              <div
                key={`${email}-${index}`}
                className="flex items-start gap-2 p-2 rounded bg-white border"
                style={{ borderColor: colors.border.light }}
              >
                <div className="mt-0.5 flex-shrink-0">
                  {getStatusIcon(status as ExecutionStatus)}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs font-medium truncate"
                    style={{ color: colors.text.primary }}
                  >
                    {email || `Result ${index + 1}`}
                  </p>
                  {subject && (
                    <p
                      className="text-xs mt-0.5 truncate"
                      style={{ color: colors.text.tertiary }}
                    >
                      {subject}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {execution.results.length > 10 && (
            <p className="text-xs text-center py-1" style={{ color: colors.text.tertiary }}>
              +{execution.results.length - 10} more results
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Run group component — one campaign (audience).
 *
 * The headline badge uses the shared campaign display status (execution state plus publish
 * gate), so a campaign reads identically here, on /campaigns, and in /workflows/:id. The
 * expandable body shows the latest run's per-step detail (run-level, legitimately run-scoped);
 * the `filter` controls whether AI/research "task" steps are hidden within that detail.
 */
const RunGroup: React.FC<{
  audienceId: number;
  runNumber?: number;
  audienceName?: string;
  onClear: () => void;
  onClose: () => void;
  defaultExpanded?: boolean;
  filter?: NotifFilter;
}> = ({ audienceId, runNumber, audienceName, onClear, onClose, defaultExpanded = false, filter = 'all' }) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  // Campaign-level display status combines execution state with the publish gate, matching
  // /campaigns even when unfinished work remains after a pause.
  const { data: audienceData, refetch: refetchAudience } = useGetAudienceById(audienceId);
  const audience = audienceData?.audience;
  const campaignStatus = getCampaignDisplayStatus(audience);
  const name = audience?.name || audienceName || `Audience ${audienceId}`;

  const totalPeople = getCampaignPeopleTotal(audience);
  // Deliberately NOT the campaign progress numerator: this drawer watches a live run and
  // its label says "contacts", so queued-into-the-workflow is the number it means.
  const processedPeople = audience?.execution_stats?.processed ?? audience?.processed_count ?? 0;

  // Latest run's per-step detail (from the recent-activity run number).
  const {
    data: detailsData,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
    isFetching,
  } = useGetRunDetails(audienceId, runNumber || 0, !!runNumber);

  // Keep polling while backend work remains in progress, including while the publish gate
  // is paused, so this open drawer can observe in-flight completion or a later resume.
  const shouldPoll = audience?.derived_status === 'in_progress';
  const isInProgress = campaignStatus === 'in_progress';
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const interval = setInterval(() => {
      refetchDetails();
      refetchAudience();
    }, 3000);
    return () => clearInterval(interval);
  }, [shouldPoll, refetchDetails, refetchAudience]);

  const run = detailsData?.data?.run;
  const executions = detailsData?.data?.executions ?? [];
  const isTestRun = !!run?.is_test_run;
  const displayRunNumber = run?.run_number ?? runNumber;

  // When filtered to campaigns only, hide AI/research "task" step cards within the run.
  const visibleExecutions = filter === 'campaigns'
    ? executions.filter((e) => !isTaskExecution(e))
    : executions;
  const taskStepsHidden = filter === 'campaigns' && visibleExecutions.length === 0 && executions.length > 0;

  if (isLoadingDetails && !audience && !detailsData) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div
          className={cn(
            "w-full group/audience cursor-pointer",
            "flex items-center justify-between px-4 py-3 transition-colors",
            "hover:bg-slate-50",
            isOpen && "bg-slate-50/50"
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            {getAudienceStatusIcon(campaignStatus)}
            <div className="flex flex-col items-start min-w-0">
              <span
                className="text-sm font-semibold truncate max-w-[200px]"
                style={{ color: colors.text.primary }}
              >
                {name}
              </span>
              <div className="flex items-center gap-2 mt-0.5">
                {displayRunNumber != null && (
                  <span className="text-xs" style={{ color: colors.text.tertiary }}>
                    Run #{displayRunNumber}
                  </span>
                )}
                {totalPeople > 0 && (
                  <span className="text-xs tabular-nums" style={{ color: colors.text.tertiary }}>
                    {processedPeople} / {totalPeople} contacts
                  </span>
                )}
                {isTestRun && (
                  <Badge variant="neutral" size="sm">Test</Badge>
                )}
                {isFetching && (
                  <RefreshCw className="h-3 w-3 animate-spin" style={{ color: colors.text.quaternary }} />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              size="xs"
              className={cn('rounded-[5px]', WORKFLOW_STATUS_BADGE_STYLE[campaignStatus] || WORKFLOW_STATUS_BADGE_STYLE.new)}
            >
              {isInProgress && (
                <span className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-current" />
              )}
              {formatWorkflowStatus(campaignStatus)}
            </Badge>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className={cn(
                "p-1 rounded transition-all",
                "opacity-0 group-hover/audience:opacity-100",
                "hover:bg-slate-200 text-slate-400 hover:text-slate-600"
              )}
              aria-label={`Clear ${name}`}
            >
              <X size={14} />
            </button>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isOpen && "rotate-180"
              )}
              style={{ color: colors.text.quaternary }}
            />
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4 space-y-3">
          {visibleExecutions.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: colors.text.tertiary }}>
              {taskStepsHidden
                ? 'Only AI/research steps in this run — hidden by the Campaigns filter'
                : 'No execution detail available'}
            </p>
          ) : (
            visibleExecutions
              .sort((a, b) => a.sequence_order - b.sequence_order)
              .map((execution, idx) => (
                <ExecutionCard
                  key={execution.execution_id ?? `exec-${idx}`}
                  execution={execution}
                  isTestRun={isTestRun}
                />
              ))
          )}
          {/* View workflow link */}
          <div className="pt-2">
            <Link
              href={`${PATH.WORKFLOWS}/${audienceId}`}
              onClick={() => onClose()}
              className="inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
              style={{ color: colors.interactive.primary }}
            >
              View workflow
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * Empty state component
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6">
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
      style={{ backgroundColor: colors.background.secondary }}
    >
      <Bell className="w-6 h-6" style={{ color: colors.text.tertiary }} />
    </div>
    <h3 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
      No Active Runs
    </h3>
    <p className="text-sm max-w-[280px]" style={{ color: colors.text.tertiary }}>
      When you start a workflow, execution progress will appear here.
    </p>
  </div>
);

type UserNotificationKind = AePreparedNotification['kind'];

/** Campaign run notices: a step was interrupted, or its contacts were handed back to the campaign. */
const CAMPAIGN_NOTICE_KINDS: ReadonlySet<UserNotificationKind> = new Set<UserNotificationKind>([
  'campaign_step_interrupted',
  'campaign_contacts_returned',
]);

/** Icon, icon tone and call to action for each user-notification kind. */
const NOTIFICATION_KIND_DISPLAY: Record<UserNotificationKind, { icon: LucideIcon; iconClassName: string; cta: string }> = {
  ae_bundle_ready: { icon: Check, iconClassName: 'bg-[#EEF4FF] text-[#0637B8]', cta: 'Review now →' },
  loop_heads_up: { icon: Repeat, iconClassName: 'bg-[#EEF4FF] text-[#0637B8]', cta: 'See this month\'s updates →' },
  campaign_step_interrupted: { icon: AlertTriangle, iconClassName: 'bg-amber-50 text-amber-700', cta: 'Open campaign →' },
  campaign_contacts_returned: { icon: RotateCcw, iconClassName: 'bg-[#EEF4FF] text-[#0637B8]', cta: 'Open campaign →' },
  loop_needs_attention: { icon: AlertTriangle, iconClassName: 'bg-amber-50 text-amber-700', cta: 'Open your loop →' },
};

/**
 * Main notifications drawer component.
 *
 * Shows the current user's own campaigns with recent execution activity (server-scoped to the
 * same set as /campaigns: own + non-ephemeral, no GTM "Task:" rows or other members' runs).
 * Real-time WebSocket notifications trigger a refresh of that list so newly-started runs appear
 * promptly, but the displayed set always comes from the scoped recent-activity feed. The
 * "All / Campaigns only" filter additionally hides AI/research task steps within each run.
 */
const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { notifications, clearNotificationsForAudience, clearAllNotifications } = useNotifications();
  const workspace = useWorkspace();
  const isAe = workspace?.isAe === true;
  const aePreparedQuery = useAePreparedNotifications(
    isOpen,
    false,
    workspace?.activeWorkspace?.vendorid ?? null,
  );
  const markAeRead = useMarkAePreparedNotificationRead();
  const markAllAeRead = useMarkAllAePreparedNotificationsRead();
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const confirmTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [dismissedAudienceIds, setDismissedAudienceIds] = useState<Set<number>>(new Set());

  // Panel filter: 'all' (default, every step) or 'campaigns' (email + campaign only, hide
  // AI/research task steps). Persisted per browser so an SDR's preference sticks across sessions.
  const [filter, setFilter] = useState<NotifFilter>('all');
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(NOTIF_FILTER_STORAGE_KEY);
      if (saved === 'campaigns' || saved === 'all') setFilter(saved);
    } catch {
      // localStorage unavailable (SSR/private mode) — fall back to default 'all'.
    }
  }, []);
  const handleSetFilter = useCallback((next: NotifFilter) => {
    setFilter(next);
    try {
      window.localStorage.setItem(NOTIF_FILTER_STORAGE_KEY, next);
    } catch {
      // Non-fatal: preference just won't persist.
    }
  }, []);

  // Scoped recent activity (own, non-ephemeral campaigns) — the single source for the list.
  const { data: recentActivity, refetch: refetchRecentActivity } = useGetRecentActivity(isOpen);

  // Reset confirm state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmClearAll(false);
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    }
  }, [isOpen]);

  // Live updates: when a real-time notification arrives, refresh the scoped list so a
  // just-started run shows up (the list stays scoped — WS is only a refetch trigger).
  const notificationCount = notifications.length;
  useEffect(() => {
    if (isOpen) refetchRecentActivity();
  }, [notificationCount, isOpen, refetchRecentActivity]);

  // Build the displayed campaign list from the scoped feed (recency order), minus dismissed.
  const items = useMemo(() => {
    const audiences = recentActivity?.data?.audiences ?? [];
    return audiences
      .filter((item) => !dismissedAudienceIds.has(item.audienceId))
      .map((item) => ({
        audienceId: item.audienceId,
        runNumber: item.latestRunNumber,
        name: item.audienceName,
        ts: new Date(item.latestExecutedAt).getTime(),
      }))
      .sort((a, b) => b.ts - a.ts);
  }, [recentActivity, dismissedAudienceIds]);

  const aePrepared = aePreparedQuery.data?.items ?? [];
  const aeUnreadCount = aePreparedQuery.data?.unread_count ?? 0;
  const hasNotifications = items.length > 0 || aePrepared.length > 0;
  // The section holds AE bundles and the monthly loop heads-up; name it for what is in it.
  const onlyLoopUpdates = aePrepared.length > 0
    && aePrepared.every((notification) => notification.kind === 'loop_heads_up' || notification.kind === 'loop_needs_attention');
  const hasCampaignNotices = aePrepared.some((notification) => CAMPAIGN_NOTICE_KINDS.has(notification.kind));
  const onlyCampaignNotices = hasCampaignNotices
    && aePrepared.every((notification) => CAMPAIGN_NOTICE_KINDS.has(notification.kind));
  // "Prepared for you" only when everything unread is prepared work; the count covers every
  // unread kind, so a mix reads as "new" rather than overstating what was prepared.
  const unreadNotices = aePrepared.filter((notification) => !notification.read_at);
  const hasUnreadPreparedWork = unreadNotices.length > 0
    && unreadNotices.every((notification) => notification.kind === 'ae_bundle_ready');
  const sectionLabel = onlyLoopUpdates
    ? 'Your nurture loop'
    : onlyCampaignNotices
      ? 'Campaign updates'
      : hasCampaignNotices ? 'Updates' : 'Prepared for you';
  const headerBadge = aeUnreadCount > 0
    ? `${aeUnreadCount} ${hasUnreadPreparedWork ? 'prepared for you' : 'new'}`
    : items.length > 0
      ? `${items.length} campaign${items.length !== 1 ? 's' : ''}`
      : null;
  const canClear = items.length > 0 || aeUnreadCount > 0;

  // Clear a single audience: clear WebSocket notifications + dismiss from the list
  const handleClearAudience = useCallback((audienceId: number) => {
    clearNotificationsForAudience(audienceId);
    setDismissedAudienceIds((prev) => new Set(prev).add(audienceId));
  }, [clearNotificationsForAudience]);

  // Clear all: clear WebSocket notifications + dismiss all entries
  const handleClearAllFull = useCallback(() => {
    clearAllNotifications();
    setDismissedAudienceIds(new Set(items.map((i) => i.audienceId)));
    if (aeUnreadCount > 0) markAllAeRead.mutate();
  }, [clearAllNotifications, items, aeUnreadCount, markAllAeRead]);

  // Handle clear all with inline confirmation
  const handleClearAll = useCallback(() => {
    if (!confirmClearAll) {
      setConfirmClearAll(true);
      confirmTimerRef.current = setTimeout(() => {
        setConfirmClearAll(false);
      }, 3000);
      return;
    }
    handleClearAllFull();
    setConfirmClearAll(false);
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  }, [confirmClearAll, handleClearAllFull]);

  // Header content
  const headerContent = (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" style={{ color: colors.text.primary }} />
          {headerBadge ? (
            <Badge variant="info" size="sm">
              {headerBadge}
            </Badge>
          ) : null}
        </div>
        {canClear ? (
          <button
            onClick={handleClearAll}
            className={cn(
              "text-xs font-medium px-2 py-1 rounded transition-all",
              confirmClearAll
                ? "bg-red-50 text-red-600 hover:bg-red-100"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            )}
          >
            {confirmClearAll ? 'Confirm clear?' : 'Clear all'}
          </button>
        ) : null}
      </div>
      {items.length > 0 && (
        <div className="flex items-center gap-1 rounded-md bg-slate-100 p-0.5 w-fit" role="tablist" aria-label="Notification filter">
          {([
            { key: 'all', label: 'All' },
            { key: 'campaigns', label: 'Campaigns only' },
          ] as { key: NotifFilter; label: string }[]).map((opt) => (
            <button
              key={opt.key}
              role="tab"
              aria-selected={filter === opt.key}
              onClick={() => handleSetFilter(opt.key)}
              className={cn(
                "text-xs font-medium px-2.5 py-1 rounded transition-all",
                filter === opt.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <BaseSheet
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Notifications"
      width="md"
      className="!max-w-[480px]"
      headerContent={headerContent}
      bodyClassName="overflow-y-auto"
    >
      {!hasNotifications ? (
        <EmptyState />
      ) : (
        <>
          {aePrepared.length > 0 ? (
            <section className="border-b border-slate-200 p-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                {sectionLabel}
              </div>
              <div className="space-y-2">
                {aePrepared.map((notification) => {
                  const display = NOTIFICATION_KIND_DISPLAY[notification.kind] ?? NOTIFICATION_KIND_DISPLAY.ae_bundle_ready;
                  return (
                  <Link
                    key={notification.id}
                    href={notification.href}
                    onClick={() => {
                      if (!notification.read_at) markAeRead.mutate(notification.id);
                      onClose();
                    }}
                    className={cn(
                      'block rounded-xl border px-3.5 py-3 transition-colors hover:bg-slate-50',
                      notification.read_at ? 'border-slate-200 bg-white' : 'border-[#C7D7FE] bg-[#F7F9FF]',
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', display.iconClassName)}>
                        <display.icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">{notification.title}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">{notification.body}</span>
                        <span className="mt-1 block text-[11px] font-medium text-[#0637B8]">
                          {display.cta}
                        </span>
                      </span>
                    </div>
                  </Link>
                  );
                })}
              </div>
            </section>
          ) : null}
          {items.length > 0 ? (
            <div className="divide-y" style={{ borderColor: colors.border.light }}>
              {items.map((item, index) => (
                <RunGroup
                  key={`${item.audienceId}-${item.ts}`}
                  audienceId={item.audienceId}
                  runNumber={item.runNumber}
                  audienceName={item.name}
                  onClear={() => handleClearAudience(item.audienceId)}
                  onClose={onClose}
                  defaultExpanded={index === 0}
                  filter={filter}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
    </BaseSheet>
  );
};

export default NotificationsDrawer;
